import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../utils/api';
import { getStoredItem } from '../utils/authStorage';
import logo from '../logo.png';

const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
  </svg>
);

const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
);

const EyeIcon = ({ off }) => off ? (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
) : (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const features = [
  { icon: '📊', label: 'Smart Dashboard', desc: 'Real-time business insights' },
  { icon: '🧾', label: 'Easy Billing', desc: 'GST invoices in seconds' },
  { icon: '👥', label: 'CRM Built-in', desc: 'Manage customers & leads' },
  { icon: '📦', label: 'Inventory', desc: 'Stock alerts & management' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [shopName, setShopName] = useState(() => {
    if (typeof window === 'undefined') return '';
    return getStoredItem('last_shop_name') || '';
  });
  const [shopSuggestions, setShopSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [userType, setUserType] = useState('shop'); // 'shop' or 'admin'
  const [loginRole, setLoginRole] = useState('owner'); // 'owner' or 'staff'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { darkMode, toggleDark, colorKey, setColor, color, themes } = useTheme();
  const formRef = useRef(null);
  const shopInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const themeBtnRef = useRef(null);
  const themeMenuRef = useRef(null);
  const [showThemeMenu, setShowThemeMenu] = useState(false);

  const syncAutofilledValues = useCallback(() => {
    const nextShopName = shopInputRef.current?.value ?? '';
    const nextEmail = emailInputRef.current?.value ?? '';
    const nextPassword = passwordInputRef.current?.value ?? '';

    if (nextShopName !== shopName) setShopName(nextShopName);
    if (nextEmail !== email) setEmail(nextEmail);
    if (nextPassword !== password) setPassword(nextPassword);
  }, [email, password, shopName]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          shopInputRef.current && !shopInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
      if (showThemeMenu &&
          themeMenuRef.current && !themeMenuRef.current.contains(e.target) &&
          themeBtnRef.current && !themeBtnRef.current.contains(e.target)) {
        setShowThemeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('touchstart', handleClick);
    };
  }, [showThemeMenu]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const timers = [150, 500, 1200].map(delay => setTimeout(syncAutofilledValues, delay));
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        syncAutofilledValues();
      }
    };
    window.addEventListener('pageshow', syncAutofilledValues);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('pageshow', syncAutofilledValues);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [syncAutofilledValues]);

  // Search shops for login autocomplete (both owner and staff)
  useEffect(() => {
    const normalizedShopName = shopName.trim();
    if (userType !== 'shop' || normalizedShopName.length < 3) {
      setShopSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await authAPI.searchShops(normalizedShopName);
        setShopSuggestions(res.data.shops || []);
        setShowSuggestions(true);
      } catch {
        setShopSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [shopName, userType]);

  const getFormValues = () => {
    const formData = formRef.current ? new FormData(formRef.current) : null;
    const nextShopName = String(formData?.get('shop_name') ?? shopInputRef.current?.value ?? shopName ?? '').trim();
    const nextEmail = String(formData?.get('email') ?? emailInputRef.current?.value ?? email ?? '').trim().toLowerCase();
    const nextPassword = String(formData?.get('password') ?? passwordInputRef.current?.value ?? password ?? '');

    setShopName(nextShopName);
    setEmail(nextEmail);
    setPassword(nextPassword);

    return { nextShopName, nextEmail, nextPassword };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { nextShopName, nextEmail, nextPassword } = getFormValues();

    let result;
    if (userType === 'admin') {
      result = await login(nextEmail, nextPassword, 'admin');
    } else if (loginRole === 'staff') {
      if (!nextShopName) {
        setError('Please enter your shop name');
        setLoading(false);
        return;
      }
      result = await login(nextEmail, nextPassword, 'staff', nextShopName);
    } else {
      if (!nextShopName) {
        setError('Please enter your shop name to login');
        setLoading(false);
        return;
      }
      result = await login(nextEmail, nextPassword, 'client', nextShopName);
    }

    if (result.success) {
      navigate('/dashboard', { replace: true });
    } else {
      setError(result.error || 'Invalid credentials');
    }
    setLoading(false);
  };

  const bg = darkMode ? '#0F172A' : color.bg;
  const cardBg = darkMode ? '#1E293B' : '#FFFFFF';
  const textPrimary = darkMode ? '#F1F5F9' : '#0F172A';
  const textMuted = darkMode ? '#94A3B8' : '#64748B';
  const borderColor = darkMode ? '#334155' : '#E2E8F0';
  const inputBg = darkMode ? '#0F172A' : '#F8FAFC';
  const accent = color.swatch;

  return (
    <div className="login-shell animate-fadeIn" style={{ minHeight: '100dvh', background: bg, transition: 'background 0.3s', fontFamily: 'system-ui,-apple-system,sans-serif' }}>

      {/* Top Bar */}
      <div className="login-topbar" style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: isMobile ? '10px 12px' : '10px 24px',
        background: darkMode ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${borderColor}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src={logo} alt="Smart Vyapari" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }} />
          <span style={{ fontWeight: 700, fontSize: 16, color: textPrimary, letterSpacing: '-0.02em' }}>Smart Vyapari</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '6px 14px', borderRadius: 8, border: `1px solid ${borderColor}`,
              background: 'transparent', cursor: 'pointer',
              color: textMuted, fontSize: 13, fontWeight: 600,
              transition: 'all 0.2s',
            }}
          >
            ← Home
          </button>
          <button
            ref={themeBtnRef}
            onClick={() => setShowThemeMenu(v => !v)}
            title="Theme & Colors"
            style={{
              width: 36, height: 36, borderRadius: 8, border: `1px solid ${borderColor}`,
              background: darkMode ? '#334155' : '#F1F5F9', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: darkMode ? '#F1F5F9' : '#475569', transition: 'all 0.2s',
            }}
          >
            {darkMode ? <SunIcon /> : <MoonIcon />}
          </button>
          {showThemeMenu && (
            <div
              ref={themeMenuRef}
              style={{
                position: 'absolute', top: 44, right: 0, zIndex: 80,
                background: cardBg, border: `1px solid ${borderColor}`,
                borderRadius: 10, padding: 10, minWidth: isMobile ? 182 : 210,
                boxShadow: '0 12px 28px rgba(0,0,0,0.2)',
              }}
            >
              <button
                onClick={toggleDark}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 10px', borderRadius: 8, border: `1px solid ${borderColor}`,
                  background: darkMode ? '#334155' : '#F1F5F9', cursor: 'pointer',
                  color: textPrimary, fontSize: 12, fontWeight: 600, marginBottom: 10,
                }}
              >
                <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                {darkMode ? <SunIcon /> : <MoonIcon />}
              </button>
              <div style={{ fontSize: 11, color: textMuted, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase' }}>Color Theme</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(themes).map(([key, t]) => (
                  <button
                    key={key}
                    title={t.name}
                    onClick={() => setColor(key)}
                    style={{
                      width: 22, height: 22, borderRadius: '50%',
                      background: t.swatch, border: 'none', cursor: 'pointer',
                      outline: colorKey === key ? `3px solid ${t.swatch}` : 'none',
                      outlineOffset: 2,
                      transition: 'outline 0.15s',
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Layout */}
      <div style={{ display: 'flex', minHeight: '100dvh', paddingTop: 56 }}>

        {/* Left Panel */}
        <div style={{
          flex: 1,
          display: isMobile ? 'none' : 'flex',
          flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          padding: '60px 48px',
          background: darkMode
            ? `linear-gradient(145deg, ${color.dark} 0%, #0F172A 100%)`
            : `linear-gradient(145deg, ${accent} 0%, ${accent}cc 100%)`,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', top: -150, right: -150 }} />
          <div style={{ position: 'absolute', width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', bottom: 30, left: -100 }} />

          <div style={{ position: 'relative', zIndex: 1, maxWidth: 440, width: '100%' }}>
            <div style={{
              width: 72, height: 72, borderRadius: 20, marginBottom: 32,
              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36,
              border: '1px solid rgba(255,255,255,0.3)',
            }}>🏪</div>

            <h1 style={{ fontSize: 40, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 14, letterSpacing: '-0.03em' }}>
              Smart Vyapari<br /><span style={{ opacity: 0.85, fontSize: 28 }}>Smart software for smart business owners.</span>
            </h1>
            <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.75)', marginBottom: 44, lineHeight: 1.7 }}>
              All-in-one billing, inventory and customer management — built for Indian shop owners.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {features.map(f => (
                <div key={f.label} style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: 'rgba(255,255,255,0.13)', backdropFilter: 'blur(8px)',
                  padding: '13px 16px', borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.2)',
                }}>
                  <span style={{ fontSize: 22 }}>{f.icon}</span>
                  <div>
                    <div style={{ color: '#fff', fontWeight: 600, fontSize: 14 }}>{f.label}</div>
                    <div style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12 }}>{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                Made with ♥ by <strong style={{ color: '#fff' }}>@Gaurav_Komarewar</strong><br />
                komarewargaurav@gmail.com · +91 88300 77989
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div style={{
          width: '100%', maxWidth: isMobile ? '100%' : 500,
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          padding: isMobile ? '22px 14px 20px' : '48px 52px',
          background: cardBg,
          boxShadow: isMobile ? 'none' : (darkMode ? '-4px 0 40px rgba(0,0,0,0.4)' : '-4px 0 40px rgba(0,0,0,0.06)'),
        }}>

          <div style={{ marginBottom: 24 }}>
            <h2 style={{ fontSize: 28, fontWeight: 800, color: textPrimary, marginBottom: 6, letterSpacing: '-0.02em' }}>
              Welcome back 👋
            </h2>
            <p style={{ color: textMuted, fontSize: 14 }}>Sign in to your account to continue</p>
          </div>

          {/* Primary toggle: Shop / Admin */}
          <div style={{
            display: 'flex', background: inputBg, borderRadius: 10,
            padding: 4, marginBottom: 20, border: `1px solid ${borderColor}`,
          }}>
            {[['shop', '🛍️ Shop'], ['admin', '⚙️ Admin']].map(([val, label]) => (
              <button key={val} onClick={() => { setUserType(val); setError(''); setEmail(''); setPassword(''); }} style={{
                flex: 1, padding: '9px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: 13, transition: 'all 0.2s',
                background: userType === val ? accent : 'transparent',
                color: userType === val ? '#fff' : textMuted,
                boxShadow: userType === val ? `0 2px 8px ${accent}44` : 'none',
              }}>{label}</button>
            ))}
          </div>

          {/* Shop mode: Shop Name + Owner/Staff toggle */}
          {userType === 'shop' && (
            <>
              {/* Shop Name Field — REQUIRED for all shop logins */}
              <div style={{ marginBottom: 16, position: 'relative' }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textMuted, marginBottom: 6 }}>
                  Shop Name <span style={{ color: '#EF4444' }}>*</span>
                </label>
                <input
                  ref={shopInputRef}
                  form="login-form"
                  name="shop_name"
                  type="text"
                  value={shopName}
                  onChange={e => setShopName(e.target.value)}
                  onInput={e => setShopName(e.target.value)}
                  onAnimationStart={syncAutofilledValues}
                  required
                  autoComplete="organization"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="next"
                  placeholder="Enter your shop name"
                  style={{
                    width: '100%', padding: '11px 14px', borderRadius: 8, fontSize: isMobile ? 16 : 14,
                    border: `1.5px solid ${borderColor}`, background: inputBg, color: textPrimary,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 3px ${accent}22`; if (shopSuggestions.length > 0) setShowSuggestions(true); }}
                  onBlur={e => { e.target.style.borderColor = borderColor; e.target.style.boxShadow = 'none'; }}
                />
                {/* Shop autocomplete dropdown */}
                {showSuggestions && shopSuggestions.length > 0 && (
                  <div ref={suggestionsRef} style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                    background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 180, overflowY: 'auto',
                    marginTop: 4,
                  }}>
                    {shopSuggestions.map((name, i) => (
                      <div key={i} onClick={() => { setShopName(name); setShowSuggestions(false); }}
                        style={{
                          padding: '10px 14px', cursor: 'pointer', fontSize: 13, color: textPrimary,
                          borderBottom: i < shopSuggestions.length - 1 ? `1px solid ${borderColor}` : 'none',
                        }}
                        onMouseEnter={e => e.target.style.background = inputBg}
                        onMouseLeave={e => e.target.style.background = 'transparent'}
                      >
                        🏪 {name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Owner / Staff sub-toggle */}
              <div style={{
                display: 'flex', background: inputBg, borderRadius: 10,
                padding: 3, marginBottom: 20, border: `1px solid ${borderColor}`,
              }}>
                {[['owner', '👤 Shop Owner'], ['staff', '🧑‍💼 Staff']].map(([val, label]) => (
                  <button key={val} onClick={() => { setLoginRole(val); setError(''); setEmail(''); setPassword(''); }} style={{
                    flex: 1, padding: '8px 0', borderRadius: 7, border: 'none', cursor: 'pointer',
                    fontWeight: 600, fontSize: 12, transition: 'all 0.2s',
                    background: loginRole === val ? (darkMode ? '#334155' : '#E2E8F0') : 'transparent',
                    color: loginRole === val ? textPrimary : textMuted,
                  }}>{label}</button>
                ))}
              </div>
            </>
          )}

          {error && (
            <div style={{
              background: darkMode ? '#3B1818' : '#FEF2F2', border: `1px solid ${darkMode ? '#7F1D1D' : '#FECACA'}`,
              color: '#DC2626',
              padding: '10px 14px', borderRadius: 8, marginBottom: 18, fontSize: 13,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              ⚠️ {error}
            </div>
          )}

          <form id="login-form" ref={formRef} onSubmit={handleSubmit} autoComplete="on" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textMuted, marginBottom: 6 }}>
                {userType === 'admin' ? 'Admin Email' : loginRole === 'staff' ? 'Staff Email / Login ID' : 'Owner Email'}
              </label>
              <input
                ref={emailInputRef}
                name="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onInput={e => setEmail(e.target.value)}
                onAnimationStart={syncAutofilledValues}
                required
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                inputMode="email"
                enterKeyHint="next"
                placeholder={userType === 'admin' ? 'admin@example.com' : loginRole === 'staff' ? 'staff@example.com' : 'owner@example.com'}
                style={{
                  width: '100%', padding: '11px 14px', borderRadius: 8, fontSize: isMobile ? 16 : 14,
                  border: `1.5px solid ${borderColor}`, background: inputBg, color: textPrimary,
                  outline: 'none', boxSizing: 'border-box',
                }}
                onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 3px ${accent}22`; }}
                onBlur={e => { e.target.style.borderColor = borderColor; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: textMuted, marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  ref={passwordInputRef}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onInput={e => setPassword(e.target.value)}
                  onAnimationStart={syncAutofilledValues}
                  required
                  autoComplete="current-password"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="go"
                  placeholder="••••••••"
                  style={{
                    width: '100%', padding: '11px 42px 11px 14px', borderRadius: 8, fontSize: isMobile ? 16 : 14,
                    border: `1.5px solid ${borderColor}`, background: inputBg, color: textPrimary,
                    outline: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={e => { e.target.style.borderColor = accent; e.target.style.boxShadow = `0 0 0 3px ${accent}22`; }}
                  onBlur={e => { e.target.style.borderColor = borderColor; e.target.style.boxShadow = 'none'; }}
                />
                <button type="button" onClick={() => setShowPassword(s => !s)} style={{
                  position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: textMuted,
                  display: 'flex', alignItems: 'center',
                }}>
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px 0', borderRadius: 10, border: 'none',
                background: loading ? `${accent}88` : accent,
                color: '#fff', fontWeight: 700, fontSize: 15, cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : `0 4px 18px ${accent}44`,
                transition: 'all 0.2s', marginTop: 4,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              {loading ? (
                <>
                  <span style={{
                    width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid #fff', borderRadius: '50%',
                    display: 'inline-block', animation: 'ims-spin 0.8s linear infinite',
                  }} />
                  Signing in…
                </>
              ) : (
                userType === 'admin' ? 'Admin Sign In →' :
                loginRole === 'staff' ? 'Staff Sign In →' : 'Shop Owner Sign In →'
              )}
            </button>
          </form>

          {/* Login mode info */}
          <div style={{
            marginTop: 18, padding: '12px 16px', borderRadius: 8,
            background: darkMode ? '#0F172A' : color.bg,
            border: `1px dashed ${accent}66`, fontSize: 12, color: textMuted,
          }}>
            {userType === 'admin' ? (
              <span>⚙️ <strong style={{ color: textPrimary }}>Admin Panel</strong> — Manage shops, subscriptions & services</span>
            ) : loginRole === 'staff' ? (
              <span>🧑‍💼 <strong style={{ color: textPrimary }}>Staff Login</strong> — Enter your shop name, then your staff credentials</span>
            ) : (
              <span>👤 <strong style={{ color: textPrimary }}>Shop Owner Login</strong> — Enter your shop name + email + password</span>
            )}
          </div>

          {/* Footer */}
          <div style={{ marginTop: 36, paddingTop: 20, borderTop: `1px solid ${borderColor}`, textAlign: 'center' }}>
            <p style={{ fontSize: 12, color: textMuted, marginBottom: 3 }}>
              Made with ♥ by <strong style={{ color: accent }}>@Gaurav_Komarewar</strong>
            </p>
            <p style={{ fontSize: 11, color: textMuted }}>
              ✉ komarewargaurav@gmail.com &nbsp;·&nbsp; 📱 +91 88300 77989
            </p>
          </div>
        </div>
      </div>

      <style>{`@keyframes ims-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Login;
